import type { Allocation } from '@talos/kyoko';
import { AllocationValueTypeEnum, runValidationV1, toBigWithDefault } from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import type { AnySchema } from 'yup-v1-latest';
import { array, date, object, string } from 'yup-v1-latest';
import type { AdjustMarketAccountPositionsForm } from './AdjustMarketAccountPositionsDialog';

export const useAdjustMarketAccountPositionsValidation = (form: AdjustMarketAccountPositionsForm) => {
  const [errors, setErrors] = useState<Partial<Record<keyof AdjustMarketAccountPositionsForm, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof AdjustMarketAccountPositionsForm, boolean>>>({});

  const setAllTouched = useCallback(
    () =>
      setTouched({
        type: true,
        marketAccount: true,
        asset: true,
        amount: true,
        timestamp: true,
        allocationMode: true,
        subAccountAllocations: true,
        allocationValueType: true,
      } satisfies Record<keyof AdjustMarketAccountPositionsForm, boolean>),
    []
  );

  useEffect(() => {
    const schemaShape: Record<string, AnySchema> = {
      type: string().required('Type is required'),
      marketAccount: string().required('Market Account is required'),
      asset: string().required('Asset is required'),
      amount: string()
        .required('Amount is required')
        .test('non-empty', 'Amount is required', val => val != null && val.trim().length > 0),
      timestamp: date().required('Timestamp is required'),
    };

    if (form.allocationMode === 'manual') {
      const amountBig = toBigWithDefault(form.amount, 0);
      const targetSum =
        form.allocationValueType === AllocationValueTypeEnum.Percentage ? toBigWithDefault('100', 0) : amountBig;

      let subAccountAllocationsSchema = array().of(
        object({
          subAccount: string().required('Please select a Sub Account'),
          value: string().default(''),
        }).typeError('Please select a Sub Account')
      );

      subAccountAllocationsSchema = subAccountAllocationsSchema.min(1, 'Please select a Sub Account');

      subAccountAllocationsSchema = subAccountAllocationsSchema.test(
        'allocations',
        form.allocationValueType === AllocationValueTypeEnum.Percentage
          ? 'Total must equal 100'
          : `Total must equal ${amountBig.toFixed()}`,
        (arr: unknown) => {
          if (!Array.isArray(arr)) {
            return false;
          }

          const sum = (arr as Allocation[]).reduce(
            (workingValue, allocation) => workingValue.plus(toBigWithDefault(allocation.value, 0)),
            toBigWithDefault(undefined, 0)
          );

          return sum.eq(targetSum);
        }
      );

      schemaShape.subAccountAllocations = subAccountAllocationsSchema;
    }

    const schema = object().shape(schemaShape);

    setErrors(prev => {
      const next = runValidationV1(schema, form);
      return isEqual(prev, next) ? prev : next;
    });
  }, [form]);

  return { errors, touched, setTouched, setAllTouched };
};
